import { useRef, useEffect } from 'react'
import { EditorView, lineNumbers, highlightActiveLine, highlightActiveLineGutter, keymap, placeholder } from '@codemirror/view'
import { EditorState } from '@codemirror/state'
import { indentWithTab, insertNewlineAndIndent, history, historyKeymap } from '@codemirror/commands'
import styles from './SourceView.module.css'

interface SourceViewProps {
    source: string
    onChange: (value: string) => void
    unitCount?: number
    isProcessing?: boolean
    viewMode?: 'editor' | 'cards'
    onViewModeChange?: (mode: 'editor' | 'cards') => void
    hideEditor?: boolean
}

/** CodeMirror theme matching EN's dark UI */
const enTheme = EditorView.theme({
    '&': {
        height: '100%',
        fontSize: '13px',
        fontFamily: 'var(--font-mono)',
        backgroundColor: 'transparent',
    },
    '.cm-content': {
        caretColor: 'var(--accent)',
        fontFamily: 'var(--font-mono)',
        padding: '12px 0',
    },
    '.cm-line': {
        padding: '0 14px',
    },
    '&.cm-focused .cm-cursor': {
        borderLeftColor: 'var(--accent)',
    },
    '&.cm-focused .cm-selectionBackground, ::selection': {
        backgroundColor: 'color-mix(in srgb, var(--accent) 25%, transparent)',
    },
    '.cm-selectionBackground': {
        backgroundColor: 'color-mix(in srgb, var(--accent) 15%, transparent)',
    },
    '.cm-gutters': {
        backgroundColor: 'transparent',
        color: 'var(--text-muted)',
        border: 'none',
        borderRight: '1px solid var(--border-subtle)',
        opacity: '0.5',
    },
    '.cm-activeLineGutter': {
        backgroundColor: 'transparent',
        color: 'var(--accent)',
        opacity: '1',
    },
    '.cm-activeLine': {
        backgroundColor: 'color-mix(in srgb, var(--accent) 4%, transparent)',
    },
    '.cm-scroller': {
        overflow: 'auto',
        fontFamily: 'var(--font-mono)',
        lineHeight: '20px',
    },
}, { dark: true })

export function SourceView({ source, onChange }: SourceViewProps) {
    const editorRef = useRef<HTMLDivElement>(null)
    const viewRef = useRef<EditorView | null>(null)

    // Initialize CodeMirror
    useEffect(() => {
        if (!editorRef.current) return

        const updateListener = EditorView.updateListener.of((update) => {
            if (update.docChanged) {
                onChange(update.state.doc.toString())
            }
        })

        const state = EditorState.create({
            doc: source,
            extensions: [
                lineNumbers(),
                highlightActiveLine(),
                highlightActiveLineGutter(),
                history(),
                keymap.of([
                    ...historyKeymap,
                    indentWithTab,
                    { key: 'Enter', run: insertNewlineAndIndent },
                ]),
                placeholder('# Describe your system\n\nserver\ndo: handle request\nneeds: http request, config\nyields: response'),
                enTheme,
                updateListener,
                EditorView.lineWrapping,
                EditorState.tabSize.of(2),
            ],
        })

        const view = new EditorView({ state, parent: editorRef.current })
        viewRef.current = view
        view.focus()

        return () => { view.destroy(); viewRef.current = null }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // Sync external source changes into CodeMirror
    useEffect(() => {
        const view = viewRef.current
        if (!view) return
        const current = view.state.doc.toString()
        if (current !== source) {
            view.dispatch({
                changes: { from: 0, to: current.length, insert: source },
            })
        }
    }, [source])

    return (
        <div className={styles.container}>
            <div ref={editorRef} className={styles.editorWrap} />
        </div>
    )
}
